import { AUTO_LAUNCH_RULES } from "../../config/gameplay";
import type { ProfileProgress } from "../progression/levels";
import type { AutoLaunchTicketSeconds, OrbitForgeSave } from "../save/types";
import type { FeedbackDraft } from "../runtime/feedback";
import { autoLaunchDurationMultiplier } from "./effects";
import { autoLaunchTicketPrice, autoLaunchUpgradePrice } from "./pricing";
import { findAutoLaunchTicket, findAutoLaunchUpgrade, ticketStorageCount, type AutoLaunchUpgradeKind } from "./catalog";

export interface AutoLaunchCommandResult {
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export function runBuyAutoLaunchTicketCommand(save: OrbitForgeSave, progress: ProfileProgress, seconds: AutoLaunchTicketSeconds): AutoLaunchCommandResult {
  const definition = findAutoLaunchTicket(seconds);
  const price = autoLaunchTicketPrice(definition, save.stats[definition.purchaseStat], progress);
  if (
    progress.effectiveLevel < AUTO_LAUNCH_RULES.unlockLevel ||
    progress.effectiveLevel < definition.unlockLevel ||
    ticketStorageCount(save) >= AUTO_LAUNCH_RULES.storageCap ||
    save.stats.Credits < price
  ) {
    return denied("auto");
  }

  save.stats.Credits -= price;
  save.stats[definition.ticketStat] += 1;
  save.stats[definition.purchaseStat] += 1;
  return {
    dirty: true,
    feedback: [{ kind: "pulse", target: "auto" }]
  };
}

export function runUseAutoLaunchTicketCommand(save: OrbitForgeSave, progress: ProfileProgress, seconds: AutoLaunchTicketSeconds): AutoLaunchCommandResult {
  const definition = findAutoLaunchTicket(seconds);
  if (
    progress.effectiveLevel < AUTO_LAUNCH_RULES.unlockLevel ||
    progress.effectiveLevel < definition.unlockLevel ||
    save.autoLaunch.activeRemainingSeconds > 0 ||
    save.stats[definition.ticketStat] <= 0
  ) {
    return denied("auto");
  }

  consumeTicket(save, seconds);
  return {
    dirty: true,
    feedback: [{ kind: "pulse", target: "auto" }]
  };
}

export function runBuyAutoLaunchUpgradeCommand(save: OrbitForgeSave, progress: ProfileProgress, kind: AutoLaunchUpgradeKind): AutoLaunchCommandResult {
  const definition = findAutoLaunchUpgrade(kind);
  const level = save.stats[definition.levelStat];
  const price = autoLaunchUpgradePrice(definition, level);
  if (
    progress.effectiveLevel < AUTO_LAUNCH_RULES.unlockLevel ||
    progress.effectiveLevel < definition.unlockLevel ||
    level >= definition.maxLevel ||
    save.stats.Credits < price
  ) {
    return denied("auto");
  }

  save.stats.Credits -= price;
  save.stats[definition.levelStat] += 1;
  return {
    dirty: true,
    feedback: [{ kind: "pulse", target: "auto" }]
  };
}

export function runToggleAutoExtendCommand(save: OrbitForgeSave, progress: ProfileProgress): AutoLaunchCommandResult {
  if (progress.effectiveLevel < AUTO_LAUNCH_RULES.unlockLevel) {
    return denied("auto");
  }

  save.autoLaunch.autoExtendEnabled = !save.autoLaunch.autoExtendEnabled;
  return {
    dirty: true,
    feedback: [{ kind: "pulse", target: "auto" }]
  };
}

export function consumeBestAutoExtendTicket(save: OrbitForgeSave): AutoLaunchTicketSeconds | null {
  for (const seconds of [600, 300, 120, 60] as const) {
    const definition = findAutoLaunchTicket(seconds);
    if (save.stats[definition.ticketStat] > 0) {
      consumeTicket(save, seconds);
      return seconds;
    }
  }
  return null;
}

function consumeTicket(save: OrbitForgeSave, seconds: AutoLaunchTicketSeconds): void {
  const definition = findAutoLaunchTicket(seconds);
  save.stats[definition.ticketStat] -= 1;
  save.autoLaunch.activeTicketSeconds = seconds;
  save.autoLaunch.activeRemainingSeconds = Math.ceil(seconds * autoLaunchDurationMultiplier(save));
  save.autoLaunch.spawnBudget = 0;
}

function denied(target: string): AutoLaunchCommandResult {
  return {
    dirty: false,
    feedback: [{ kind: "denied", target }]
  };
}
